import { useState } from "react";
import { Bell, BellSlash, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { usePush, PUSH_SUPPORTED } from "@/lib/push";

/* Tiny popover bell shown in the top-right of admin pages.
 * Clicking it opens a dropdown with subscribe/unsubscribe + a Test push button.
 */
export default function PushBell({ className = "" }) {
  const [open, setOpen] = useState(false);
  const { supported, permission, subscribed, busy, error, subscribe, unsubscribe, sendTest } = usePush();
  const [testStatus, setTestStatus] = useState(null);

  if (!supported || !PUSH_SUPPORTED) return null;

  const onTest = async () => {
    setTestStatus("sending");
    const r = await sendTest();
    if (r?.error || r?.sent === 0) setTestStatus(`error:${r?.error || "no_subscribers"}`);
    else setTestStatus(`ok:${r?.sent || 0}`);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        data-testid="push-bell-toggle"
        onClick={() => setOpen((v) => !v)}
        className={`w-8 h-8 flex items-center justify-center rounded-sm border ${
          subscribed ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-blue-700"
        } transition-colors`}
        title={subscribed ? "Push notifications: ON" : "Push notifications: OFF"}
      >
        {subscribed ? <Bell size={16} weight="fill" /> : <BellSlash size={16} weight="bold" />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid="push-bell-menu"
            className="absolute right-0 top-10 z-50 w-72 bg-white border border-slate-200 rounded-sm shadow-xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-bold font-['Chivo']">Browser push</h4>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={14} weight="bold" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Get instant alerts for hot leads and new warranty claims, even when this tab is closed.
            </p>

            {permission === "denied" && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-2 mb-2">
                Notifications are blocked in browser settings. Enable them in your site settings to subscribe.
              </div>
            )}
            {error && permission !== "denied" && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-2 mb-2">
                {String(error)}
              </div>
            )}

            <div className="flex flex-col gap-2">
              {!subscribed ? (
                <button
                  data-testid="push-bell-subscribe"
                  disabled={busy || permission === "denied"}
                  onClick={subscribe}
                  className="text-xs font-bold uppercase tracking-wider bg-blue-700 text-white py-2 rounded-sm hover:bg-blue-800 disabled:opacity-40 flex items-center justify-center gap-1.5"
                >
                  <Bell size={13} weight="bold" />
                  {busy ? "Subscribing…" : "Enable push notifications"}
                </button>
              ) : (
                <>
                  <button
                    data-testid="push-bell-test"
                    disabled={busy}
                    onClick={onTest}
                    className="text-xs font-bold uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-800 py-2 rounded-sm hover:bg-amber-100 flex items-center justify-center gap-1.5"
                  >
                    <PaperPlaneTilt size={13} weight="bold" />
                    Send test push
                  </button>
                  <button
                    data-testid="push-bell-unsubscribe"
                    disabled={busy}
                    onClick={unsubscribe}
                    className="text-xs font-bold uppercase tracking-wider bg-white border border-slate-300 text-slate-700 py-2 rounded-sm hover:bg-slate-100 flex items-center justify-center gap-1.5"
                  >
                    <BellSlash size={13} weight="bold" />
                    Disable
                  </button>
                </>
              )}
              {testStatus && (
                <div data-testid="push-bell-test-status"
                  className={`text-[11px] mt-1 ${testStatus.startsWith("ok") ? "text-emerald-700" : "text-rose-700"}`}>
                  {testStatus.startsWith("ok")
                    ? `Sent — check the system tray or your phone (${testStatus.slice(3)} delivery${testStatus.endsWith("1") ? "" : "ies"}).`
                    : `Couldn't send: ${testStatus.slice(6)}`}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
